import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi, billingApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  UserIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
  CurrencyPoundIcon,
} from '@heroicons/react/24/outline';

type AccountType = 'solo' | 'company';

interface AccountForm {
  name: string;
  email: string;
  password: string;
  accountType: AccountType;
  companyName: string;
  phone: string;
  seats: number;
}

const defaults: AccountForm = {
  name: '',
  email: '',
  password: '',
  accountType: 'company',
  companyName: '',
  phone: '',
  seats: 2,
};

/**
 * Full signup: Account → Plan → Verify email → Stripe Checkout.
 *
 * Why it's split like this
 * ------------------------
 *  1. We create the User + Company + TRIAL subscription in our own DB first
 *     so that the Stripe checkout session can reference a `companyId`.
 *  2. Email verification comes next. In non-prod we short-circuit it with
 *     the dev code returned by the API so the flow is testable without
 *     real email delivery.
 *  3. Once verified we redirect to Stripe Checkout for the selected plan
 *     (Solo £20 / Company £40 × seats). On success the backend webhook
 *     flips the subscription to ACTIVE and the user lands in /onboarding.
 */
export default function SignUp() {
  const navigate = useNavigate();
  const { setTokenAndUser } = useAuth();

  const [step, setStep] = useState<'account' | 'plan' | 'verify'>('account');
  const [form, setForm] = useState<AccountForm>(defaults);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  const monthlyCost = useMemo(() => {
    if (form.accountType === 'solo') return 20;
    return 40 * Math.max(2, form.seats || 2);
  }, [form.accountType, form.seats]);

  const update = <K extends keyof AccountForm>(key: K, value: AccountForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // -------------------- STEP 1: Account --------------------

  const submitAccount = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) return setError('Your name is required.');
    if (!/.+@.+\..+/.test(form.email)) return setError('Enter a valid email address.');
    if (form.password.length < 8)
      return setError('Choose a password with at least 8 characters.');
    if (form.accountType === 'company' && !form.companyName.trim())
      return setError('Company name is required for the Company plan.');
    setStep('plan');
  };

  // -------------------- STEP 2: Plan & register --------------------

  const submitPlan = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const resp = await authApi.signup({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone?.trim() || undefined,
        accountType: form.accountType,
        companyName: form.accountType === 'company' ? form.companyName.trim() : undefined,
        seats: form.accountType === 'company' ? Math.max(2, form.seats) : 1,
        subscriptionPlan: form.accountType === 'solo' ? 'starter' : 'basic',
      });
      const data = resp.data;
      if (data?.devVerificationCode) {
        setDevCode(data.devVerificationCode);
        setVerificationCode(data.devVerificationCode);
      }
      setStep('verify');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Sign-up failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------- STEP 3: Verify email + Stripe --------------------

  const verifyAndCheckout = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const resp = await authApi.verify(form.email.trim().toLowerCase(), verificationCode.trim());
      const data = resp.data;
      if (!data?.access_token) throw new Error('Verification did not return an access token.');
      setTokenAndUser(data.access_token, {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role,
        companyId: data.user.companyId,
        companyName: data.user.companyName,
        onboardingStatus: 'PENDING',
        plan: form.accountType === 'solo' ? 'SOLO' : 'COMPANY',
      });

      // Head straight into Stripe Checkout for the paid plan.
      setRedirecting(true);
      const checkoutResp = await billingApi.createCheckout({
        plan: form.accountType === 'solo' ? 'SOLO' : 'COMPANY',
        seats: form.accountType === 'company' ? Math.max(2, form.seats) : 1,
      });
      const url = checkoutResp.data?.url;
      if (!url) throw new Error('Stripe returned no checkout URL.');
      window.location.assign(url);
    } catch (err: any) {
      setRedirecting(false);
      const msg = err?.response?.data?.message || err?.message || 'Verification or checkout failed.';
      // If Stripe isn't configured yet, hop straight into onboarding so
      // the rest of the flow is still testable.
      if (msg.toLowerCase().includes('stripe')) {
        navigate('/onboarding');
        return;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-fill dev code once when we receive it, to make local QA painless.
  useEffect(() => {
    if (devCode && !verificationCode) setVerificationCode(devCode);
  }, [devCode, verificationCode]);

  const progressPct =
    step === 'account' ? 33 : step === 'plan' ? 66 : 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50 flex">
      {/* Left: form */}
      <div className="w-full lg:w-[55%] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl">
          <Link to="/" className="flex items-center gap-2 mb-10 text-slate-900">
            <span className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <ShieldCheckIcon className="w-5 h-5 text-white" />
            </span>
            <span className="text-xl font-semibold tracking-tight">Certs</span>
          </Link>

          <div className="mb-8">
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-orange-500 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] uppercase tracking-wider text-slate-500 mt-2">
              <span className={step !== 'account' ? 'text-slate-400' : 'font-semibold text-slate-900'}>
                Account
              </span>
              <span className={step === 'plan' ? 'font-semibold text-slate-900' : step === 'verify' ? 'text-slate-400' : ''}>
                Plan
              </span>
              <span className={step === 'verify' ? 'font-semibold text-slate-900' : ''}>Verify & pay</span>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg">
              {error}
            </div>
          )}

          {step === 'account' && (
            <form onSubmit={submitAccount} className="space-y-5">
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Create your account</h1>
                <p className="text-slate-600 text-sm mt-1">
                  Start your 7-day trial. Cancel any time.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => update('accountType', 'solo')}
                  className={`border rounded-xl px-4 py-3 text-left transition ${
                    form.accountType === 'solo'
                      ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-100'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <UserIcon className="w-4 h-4" />
                    <span className="font-medium text-sm text-slate-900">Solo engineer</span>
                  </div>
                  <div className="text-xs text-slate-500">£20/month — 1 user</div>
                </button>
                <button
                  type="button"
                  onClick={() => update('accountType', 'company')}
                  className={`border rounded-xl px-4 py-3 text-left transition ${
                    form.accountType === 'company'
                      ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-100'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <BuildingOffice2Icon className="w-4 h-4" />
                    <span className="font-medium text-sm text-slate-900">Company</span>
                  </div>
                  <div className="text-xs text-slate-500">£40/user — from 2 users</div>
                </button>
              </div>

              <Field label="Full name">
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  autoComplete="name"
                  required
                  className="input"
                />
              </Field>

              <Field label="Work email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  autoComplete="email"
                  required
                  className="input"
                />
              </Field>

              <Field label="Password" hint="At least 8 characters.">
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  autoComplete="new-password"
                  required
                  className="input"
                />
              </Field>

              {form.accountType === 'company' && (
                <Field label="Company name">
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={(e) => update('companyName', e.target.value)}
                    required
                    className="input"
                  />
                </Field>
              )}

              <Field label="Phone (optional)">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className="input"
                />
              </Field>

              <button
                type="submit"
                className="btn-primary w-full justify-center"
              >
                Continue
                <ArrowRightIcon className="w-4 h-4" />
              </button>

              <div className="text-sm text-slate-600 text-center">
                Already have an account?{' '}
                <Link to="/login" className="text-orange-600 font-medium">Sign in</Link>
              </div>
            </form>
          )}

          {step === 'plan' && (
            <div className="space-y-5">
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Pick your plan</h1>
                <p className="text-slate-600 text-sm mt-1">
                  You can change plans or seats any time from Settings.
                </p>
              </div>

              <PlanCard
                active={form.accountType === 'solo'}
                onSelect={() => update('accountType', 'solo')}
                title="Solo engineer"
                price="£20"
                per="per month"
                badge={null}
                features={[
                  '1 user (you)',
                  'Jobs, clients, sites, certificates',
                  'Quoting + invoicing',
                  'Stripe-powered online payments',
                  'Solo-friendly dashboard',
                ]}
              />
              <PlanCard
                active={form.accountType === 'company'}
                onSelect={() => update('accountType', 'company')}
                title="Company"
                price="£40"
                per="per user / month"
                badge="Popular"
                features={[
                  'Team seats — add engineers + admins',
                  'Engineer portal & in-field quick quotes',
                  'Price book + approval policy',
                  'In-app team messaging & client comms',
                  'Unlimited clients, sites, jobs',
                ]}
              />

              {form.accountType === 'company' && (
                <div className="border border-slate-200 bg-white rounded-xl p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium text-slate-900">How many users?</div>
                      <div className="text-xs text-slate-500">You can add more later.</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => update('seats', Math.max(2, form.seats - 1))}
                        className="w-8 h-8 rounded-lg border border-slate-200 text-slate-700"
                      >
                        −
                      </button>
                      <div className="w-10 text-center font-medium">{form.seats}</div>
                      <button
                        type="button"
                        onClick={() => update('seats', form.seats + 1)}
                        className="w-8 h-8 rounded-lg border border-slate-200 text-slate-700"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between rounded-xl bg-slate-900 text-white px-5 py-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-300">Monthly total</div>
                  <div className="text-2xl font-semibold">£{monthlyCost}</div>
                  <div className="text-xs text-slate-400">Billed monthly via Stripe. Cancel any time.</div>
                </div>
                <CurrencyPoundIcon className="w-8 h-8 text-orange-400" />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep('account')}
                  className="btn-secondary"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={submitPlan}
                  className="btn-primary flex-1 justify-center"
                >
                  {submitting ? 'Creating account…' : 'Create account'}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-5">
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Verify your email</h1>
                <p className="text-slate-600 text-sm mt-1">
                  We sent a 6-digit code to <span className="font-medium">{form.email}</span>.
                </p>
              </div>

              {devCode && (
                <div className="p-3 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-sm">
                  <strong>Dev mode:</strong> your verification code is <code>{devCode}</code>. Skip
                  SMTP by clicking Continue.
                </div>
              )}

              <Field label="Verification code">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="input text-center text-xl tracking-[0.5em] font-mono"
                />
              </Field>

              <button
                type="button"
                disabled={submitting || verificationCode.length !== 6 || redirecting}
                onClick={verifyAndCheckout}
                className="btn-primary w-full justify-center"
              >
                {redirecting ? 'Opening secure checkout…' : submitting ? 'Verifying…' : 'Continue to payment'}
                <ArrowRightIcon className="w-4 h-4" />
              </button>

              <button
                type="button"
                className="w-full text-sm text-slate-500 hover:text-slate-700"
                onClick={async () => {
                  try {
                    await authApi.resendVerification(form.email.trim().toLowerCase());
                    setError(null);
                  } catch (err: any) {
                    setError(err?.response?.data?.message || 'Could not resend code.');
                  }
                }}
              >
                Didn't get it? Resend code
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right: marketing rail */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,122,26,0.25),transparent_60%)]" />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs">
              <SparklesIcon className="w-3.5 h-3.5" />
              Built for gas & heating engineers
            </div>
            <h2 className="mt-6 text-3xl font-semibold leading-tight">
              Jobs, certificates, quotes and payments — one workflow.
            </h2>
            <p className="mt-4 text-slate-300 max-w-md">
              Your engineers issue compliant CP12/CP4/CP15/CP17 certs from the field. Your office
              quotes faster, invoices cleaner, and gets paid via Stripe.
            </p>
          </div>

          <div className="space-y-3">
            <Perk icon={<ShieldCheckIcon className="w-5 h-5" />}>
              Gas Safe compliant certificate library (CP12, CP4, CP15, CP17)
            </Perk>
            <Perk icon={<UsersIcon className="w-5 h-5" />}>
              Team seats scale with you — pay only for active users
            </Perk>
            <Perk icon={<CheckCircleIcon className="w-5 h-5" />}>
              Cancel any time from the Stripe customer portal
            </Perk>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%; padding: 10px 12px; border-radius: 10px;
          border: 1px solid #e2e8f0; background: #fff; font-size: 14px;
          transition: border-color .15s, box-shadow .15s;
        }
        .input:focus { outline: none; border-color: #FF7A1A; box-shadow: 0 0 0 3px rgba(255,122,26,0.15); }
        .btn-primary {
          display: inline-flex; align-items: center; justify-content: center; gap: 6px;
          background: #0f172a; color: #fff; padding: 10px 16px; border-radius: 10px;
          font-weight: 600; font-size: 14px;
        }
        .btn-primary:hover { background: #1e293b; }
        .btn-primary:disabled { opacity: .7; cursor: not-allowed; }
        .btn-secondary {
          display: inline-flex; align-items: center; justify-content: center; gap: 6px;
          background: #f1f5f9; color: #0f172a; padding: 10px 16px; border-radius: 10px;
          font-weight: 600; font-size: 14px;
        }
        .btn-secondary:hover { background: #e2e8f0; }
      `}</style>
    </div>
  );
}

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-700 mb-1 block">{label}</span>
    {children}
    {hint && <span className="text-xs text-slate-500 mt-1 block">{hint}</span>}
  </label>
);

const Perk: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <div className="flex items-start gap-3">
    <div className="shrink-0 w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
      {icon}
    </div>
    <div className="text-sm text-slate-200 pt-1.5">{children}</div>
  </div>
);

const PlanCard: React.FC<{
  active: boolean;
  onSelect: () => void;
  title: string;
  price: string;
  per: string;
  badge: string | null;
  features: string[];
}> = ({ active, onSelect, title, price, per, badge, features }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full text-left rounded-2xl p-5 transition border ${
      active
        ? 'border-orange-500 bg-orange-50/60 ring-2 ring-orange-100'
        : 'border-slate-200 bg-white hover:border-slate-300'
    }`}
  >
    <div className="flex items-start justify-between mb-3">
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="flex items-baseline gap-1 mt-1">
          <span className="text-2xl font-semibold text-slate-900">{price}</span>
          <span className="text-xs text-slate-500">{per}</span>
        </div>
      </div>
      {badge && (
        <span className="bg-orange-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider">
          {badge}
        </span>
      )}
    </div>
    <ul className="space-y-2">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
          <CheckCircleIcon className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          {f}
        </li>
      ))}
    </ul>
  </button>
);
